import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { Button, message, Popconfirm, Space, Table, Tag, Switch, Tabs } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useState } from "react";
import {
  useGetBusinessAddresses,
  useDeleteBusinessAddress,
  useToggleBusinessAddressActive,
} from "~/services/business-addresses";
import { TBusinessAddress, AddressType } from "~/types/business-address";
import { BusinessAddressModal } from "../modals/business-address-modal";
import { RateTypeDiscountsTab } from "./rate-type-discounts-tab";
import { capitalize } from "~/utils";

interface BusinessAddressesTabProps {}

export const BusinessAddressesTab: React.FC<BusinessAddressesTabProps> = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<TBusinessAddress | null>(null);

  const { data: businessAddresses, isLoading, refetch } = useGetBusinessAddresses(false); // Get all, not just active
  const deleteBusinessAddress = useDeleteBusinessAddress();
  const toggleActive = useToggleBusinessAddressActive();

  const handleEdit = (address: TBusinessAddress) => {
    setEditingAddress(address);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditingAddress(null);
    setModalOpen(true);
  };

  const handleDelete = async (addressId: string) => {
    try {
      await deleteBusinessAddress.mutateAsync(addressId);
      message.success("Business address deleted successfully");
      refetch();
    } catch {
      message.error("Failed to delete business address");
    }
  };

  const handleToggleActive = async (addressId: string) => {
    try {
      await toggleActive.mutateAsync(addressId);
      message.success("Business address status updated successfully");
      refetch();
    } catch {
      message.error("Failed to update business address status");
    }
  };

  const handleModalSuccess = () => {
    refetch();
  };

  const getAddressTypeColor = (type: AddressType) => {
    switch (type) {
      case AddressType.WAREHOUSE:
        return "blue";
      case AddressType.OFFICE:
        return "green";
      case AddressType.STORAGE:
        return "orange";
      default:
        return "default";
    }
  };

  const columns: ColumnsType<TBusinessAddress> = [
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: "8%",
      render: (isActive: boolean, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleActive(record.id)}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<CloseCircleOutlined />}
        />
      ),
    },
    {
      title: "Location Name",
      dataIndex: "name",
      key: "name",
      width: "15%",
      render: (name: string, record) => (
        <div>
          <div className="font-medium">{name}</div>
          {!record.isActive && (
            <Tag color="red" className="mt-1">
              Inactive
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "addressType",
      key: "addressType",
      width: "10%",
      render: (type: AddressType) => (
        <Tag color={getAddressTypeColor(type)}>
          {capitalize(type)}
        </Tag>
      ),
    },
    {
      title: "Address",
      key: "address",
      width: "25%",
      render: (_, record) => (
        <div>
          <div>{record.address}</div>
          {record.address2 && <div className="text-gray-500">{record.address2}</div>}
          <div className="text-gray-600">
            {record.city}, {record.state} {record.zip}
          </div>
        </div>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      width: "15%",
      render: (_, record) => (
        <div>
          {record.contactPerson && <div>{record.contactPerson}</div>}
          {record.phone && <div className="text-gray-600">{record.phone}</div>}
        </div>
      ),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      width: "15%",
      render: (notes: string) => notes || "-",
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: "12%",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title="Delete Business Address"
            description="Are you sure you want to delete this business address?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: "addresses",
      label: "Business Addresses",
      children: (
        <>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-medium mb-2">Business Addresses</h3>
              <p className="text-gray-500">
                Manage AHC warehouse and office locations for service tickets
              </p>
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add New Location
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={businessAddresses || []}
            rowKey="id"
            loading={isLoading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} locations`,
            }}
            scroll={{ x: 1200 }}
            className="bg-white"
          />

          <BusinessAddressModal
            open={modalOpen}
            onCancel={() => setModalOpen(false)}
            businessAddress={editingAddress}
            onSuccess={handleModalSuccess}
          />
        </>
      ),
    },
    {
      key: "rate-type-discounts",
      label: "Rate Type Discounts",
      children: <RateTypeDiscountsTab />,
    },
  ];

  return (
    <div className="space-y-6">
      <Tabs items={tabItems} defaultActiveKey="addresses" />
    </div>
  );
};
